import { useState, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import GenericTable from '../../../common/GenericTable'
import SearchWithFilter from '../../../common/SearchWithFilter'
import FiltersModal, { FilterType } from '../../../common/FiltersModal'
import EmptyPageResults from '../../../common/EmptyPageResults'
import LoadingSpinner from '../../../common/LoadingSpinner'
import Dropdown from '../../../common/Dropdown'
import SimpleButton from '../../../common/SimpleButton'
import { toast } from '../../../common/Toast'
import { DownloadIcon, FilterTableIcon, InformationIcon } from '../../../../icons'
import { downloadFileUTF8, sortModelsAlphabetically } from '../../../../utils/functions'
import { useWindowSize } from '../../../../libs/hooks'
import { useDeepCompareEffect } from '../../../../hooks/useDeepCompareEffect'
import { loadRegions } from '../../../../actions/shared'
import { loadFirmwareModels, loadFirmwareTypes } from '../../../../actions/firmware'
import {
  clearReportsMessages,
  loadSoftwareVersionRawData,
  loadSoftwareVersionStatisticsData,
  downloadSoftwareVersionReportsCSV,
  loadSoftwareVersionReportsFilterOptions,
} from '../../../../actions/reports'
import SoftwareVersionChart from './SoftwareVersionChart'
import '../../internal-reports.scss'

const orderingFields = [
  {
    field: 'model',
    order: 'asc',
    translationKey: 'model_asc',
  },
  {
    field: 'model',
    order: 'desc',
    translationKey: 'model_desc',
  },
  {
    field: 'softwareType',
    order: 'asc',
    translationKey: 'software_type_asc',
  },
  {
    field: 'softwareType',
    order: 'desc',
    translationKey: 'software_type_desc',
  },
  {
    field: 'prefix',
    order: 'asc',
    translationKey: 'prefix_asc',
  },
  {
    field: 'prefix',
    order: 'desc',
    translationKey: 'prefix_desc',
  },
  {
    field: 'latestVersion',
    order: 'asc',
    translationKey: 'latest_version_asc',
  },
  {
    field: 'latestVersion',
    order: 'desc',
    translationKey: 'latest_version_desc',
  },
  {
    field: 'totalDevices',
    order: 'asc',
    translationKey: 'total_devices_asc',
  },
  {
    field: 'totalDevices',
    order: 'desc',
    translationKey: 'total_devices_desc',
  },
  {
    field: 'updatedDevices',
    order: 'asc',
    translationKey: 'updated_devices_asc',
  },
  {
    field: 'updatedDevices',
    order: 'desc',
    translationKey: 'updated_devices_desc',
  },
  {
    field: 'updatePercentage',
    order: 'asc',
    translationKey: 'update_percentage_asc',
  },
  {
    field: 'updatePercentage',
    order: 'desc',
    translationKey: 'update_percentage_desc',
  },
]

const DeviceSoftwareVersionReports = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [softwareVersionTableData, setSoftwareVersionTableData] = useState([])
  const [orderBy, setOrderBy] = useState(null)
  const [tableOrderBy, setTableOrderBy] = useState(null)
  const [selectedSoftwareType, setSelectedSoftwareType] = useState([])
  const [selectedFilterModel, setSelectedFilterModel] = useState([])
  const [selectedFilterPrefix, setSelectedFilterPrefix] = useState([])
  const [selectedFilterRegion, setSelectedFilterRegion] = useState([])
  const [searchParam, setSearchParam] = useState('')
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showChartFiltersModal, setShowChartFiltersModal] = useState(false)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [didInitModelFilter, setDidInitModelFilter] = useState(false)
  const [chartType, setChartType] = useState('pie')
  const [expandedData, setExpandedData] = useState(0)
  const [statsVisibleCount, setStatsVisibleCount] = useState(20)
  const chartTypeKeys = ['bar', 'stackedBar', 'pie']
  const didInitRequests = useRef(false)

  const filteredTableData = useMemo(() => {
    const q = searchParam?.toLowerCase()
    let rows = softwareVersionTableData

    if (q) {
      rows = rows.filter((r) => {
        const model = (r.model || '').toLowerCase()
        const softwareType = (r.softwareType || '').toLowerCase()
        const latestVersion = (r.latestVersion || '').toLowerCase()
        const totalDevices = String(r.totalDevices ?? '').toLowerCase()
        const updatedDevices = String(r.updatedDevices ?? '').toLowerCase()
        const updatePercentage = String(r.updatePercentage ?? '').toLowerCase()
        return (
          model.includes(q) ||
          softwareType.includes(q) ||
          latestVersion.includes(q) ||
          totalDevices.includes(q) ||
          updatedDevices.includes(q) ||
          updatePercentage.includes(q)
        )
      })
    }

    if (tableOrderBy?.field === 'updatePercentage' && tableOrderBy?.order) {
      const dir = tableOrderBy.order === 'asc' ? 1 : -1
      rows = [...rows].sort((a, b) => dir * ((a.updatePercentageValue ?? 0) - (b.updatePercentageValue ?? 0)))
    }

    return rows
  }, [softwareVersionTableData, searchParam, tableOrderBy])

  const validSoftwareVersionStatistics = useMemo(() => {
    return (props.softwareVersionStatistics || []).filter((item) => Number(item.totalDevices) > 0)
  }, [props.softwareVersionStatistics])

  const chartData = validSoftwareVersionStatistics
  const hasChart = chartData.length > 0
  const hasTable = filteredTableData.length > 0
  const typeLabels = props.types?.map((t) => {
    return t.type || []
  })

  const chartFilterOptions = [
    {
      name: t('reports.chart_type'),
      type: FilterType.singleSelect,
      dataset: chartTypeKeys,
      input: chartType,
      output: (selected) => setChartType(selected),
      required: true,
    },
  ]

  useEffect(() => {
    if (didInitRequests.current) return
    didInitRequests.current = true

    if (props.firmwareModels.length === 0) {
      props.actions.loadFirmwareModels()
    }

    if (props.reportsRegions.length === 0) {
      props.actions.loadRegions()
    }

    if (props.types.length === 0) {
      props.actions.loadFirmwareTypes()
    }

    if (Object.keys(props.filters).length === 0) {
      props.actions.loadSoftwareVersionReportsFilterOptions()
    }
  }, [props.actions])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearReportsMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (didInitModelFilter) return

    if (!Array.isArray(props.g4ModelNames) || props.g4ModelNames.length === 0) return

    const blocked = ['hydrim m2', 'statim 6000b']
    const preselected = props.g4ModelNames.filter((name) => {
      const n = String(name || '').toLowerCase()
      return !blocked.some((b) => n.includes(b))
    })

    setSelectedFilterModel(preselected)
    setDidInitModelFilter(true)
  }, [didInitModelFilter, props.g4ModelNames])

  useDeepCompareEffect(() => {
    if (!shouldApplyFilters) return
    if (!didInitModelFilter) return

    if (orderBy?.field === 'updatePercentage') return

    handleLoadData(true)
  }, [didInitModelFilter, selectedSoftwareType, selectedFilterModel, selectedFilterRegion, selectedFilterPrefix, orderBy])

  useEffect(() => {
    const tableData = validSoftwareVersionStatistics.map((item, index) => {
      const total = Number(item.totalDevices) || 0
      const updated = Number(item.updatedDevices) || 0
      const pctNum = total === 0 ? 0 : (updated / total) * 100
      return {
        index,
        model: item.model,
        softwareType: item.softwareType,
        prefix: item.prefix,
        latestVersion: item.latestVersion,
        totalDevices: item.totalDevices,
        updatedDevices: item.updatedDevices,
        updatePercentage: calculatePercentage(updated, total),
        updatePercentageValue: pctNum,
      }
    })
    setSoftwareVersionTableData(tableData)
    setStatsVisibleCount(20)
  }, [validSoftwareVersionStatistics])

  const handleSearch = (value) => {
    setSearchParam((value || '').trim())
  }

  const calculatePercentage = (part, total) => {
    if (total === 0) return '0%'
    return ((part / total) * 100).toFixed(2) + '%'
  }

  const getUniqueRegions = (regions) => {
    if (!regions || regions.length === 0) {
      return []
    }

    const names = regions.map((region) => region.name)
    const uniqueNames = [...new Set(names)]
    return uniqueNames.sort((a, b) => a.localeCompare(b))
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (selectedSoftwareType.length > 0) {
      queryParams = { ...queryParams, softwareType: selectedSoftwareType }
    }

    if (selectedFilterModel.length > 0) {
      queryParams = { ...queryParams, model: selectedFilterModel }
    }

    if (selectedFilterRegion.length > 0) {
      queryParams = { ...queryParams, region: selectedFilterRegion }
    }

    if (selectedFilterPrefix.length > 0) {
      queryParams = { ...queryParams, prefix: selectedFilterPrefix }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const handleLoadData = () => {
    let queryParams = createQueryParams()

    props.actions.loadSoftwareVersionStatisticsData(queryParams).then(() => setIsFirstLoad(false))
  }

  const handleResetFilters = () => {
    setSelectedSoftwareType([])
    setSelectedFilterModel([])
    setSelectedFilterRegion([])
    setSelectedFilterPrefix([])
  }

  const handleResetChartFilters = () => {
    setChartType('pie')
  }

  const handleSort = (orderingField) => {
    if (orderingField?.field === 'updatePercentage') {
      setTableOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
      return
    }
    setTableOrderBy(null)

    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleDownload = () => {
    let queryParams = createQueryParams()

    props.actions.downloadSoftwareVersionReportsCSV(queryParams).then((res) => {
      const date = new Date().toISOString().split('T')
      const dateNow = date[0].split('-').reverse().join('_')
      const timeNow = date[1].split('.')[0].split(':').join('_')

      downloadFileUTF8({
        data: res.value,
        fileName: `software-version-downloads_${dateNow}_${timeNow}.csv`,
        fileType: 'text/csv',
      })
    })
  }

  const renderChartSection = () => {
    if (chartData) {
      return (
        <div className={`chart-section ${chartType === 'pie' ? 'pie-chart' : ''}`}>
          <div className="chart-filters-row">
            <div className="chart-title">{t('reports.software_version_chart')}</div>
            <SimpleButton className="filter-button" onClick={() => setShowChartFiltersModal(true)}>
              <div className="icon-wrapper">
                <FilterTableIcon />
              </div>
              {t('reports.chart_styles')}
            </SimpleButton>
          </div>
          <SoftwareVersionChart data={chartData} chartType={chartType} key={`chart-${chartType}`} />
          {showChartFiltersModal && (
            <div className="chart-filters-modal-container">
              <FiltersModal
                filters={chartFilterOptions}
                name="styles"
                closeFilters={() => setShowChartFiltersModal(false)}
                modalClassName="chart-filters-modal"
                resetFilters={handleResetChartFilters}
                requireAllRequiredFilters
              />
            </div>
          )}
        </div>
      )
    }
  }

  const renderSoftwareVersionTable = () => {
    if (!props.isLoading && !isFirstLoad && filteredTableData.length === 0) {
      return <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
    }

    if (!isMobile) {
      return (
        <GenericTable
          data={filteredTableData.slice(0, statsVisibleCount)}
          headers={[
            {
              title: t('model'),
              orderKey: 'model',
            },
            {
              title: t('reports.software_type'),
              orderKey: 'softwareType',
            },
            {
              title: t('prefix'),
              orderKey: 'prefix',
            },
            {
              title: t('firmware_context.latest_version'),
              orderKey: 'latestVersion',
            },
            {
              title: t('reports.total_devices'),
              orderKey: 'totalDevices',
            },
            {
              title: t('reports.updated_devices'),
              orderKey: 'updatedDevices',
            },
            {
              title: t('reports.update_percentage'),
              orderKey: 'updatePercentage',
            },
          ]}
          keys={['model', 'softwareType', 'prefix', 'latestVersion', 'totalDevices', 'updatedDevices', 'updatePercentage']}
          activeSort={tableOrderBy?.field === 'updatePercentage' ? tableOrderBy : orderBy}
          onSort={(orderingField) => handleSort(orderingField)}
          isLoading={props.isLoading}
        />
      )
    }
    return (
      <>
        {filteredTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={(tableOrderBy?.field === 'updatePercentage' ? tableOrderBy : orderBy)?.translationKey}
              onChange={(orderBy) => {
                const sortOption = orderingFields.find((f) => f.translationKey === orderBy.key)
                handleSort(sortOption)
              }}
            />
          </div>
        )}

        {filteredTableData.slice(0, statsVisibleCount).map((item, index) => (
          <div key={index} className="card" onClick={() => setExpandedData(index)}>
            <div className={'card-item' + (expandedData !== index ? ' align-center' : '')}>
              <div className="card-item-title">{t('model')}</div>
              <div className={'card-item-body' + (expandedData !== index ? ' align-center' : '')}>
                <div>{item.model}</div>
              </div>
            </div>
            {expandedData === index && (
              <>
                <div className="card-item">
                  <div className="card-item-title">{t('reports.software_type')}</div>
                  <div className="card-item-body">{item.softwareType}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('prefix')}</div>
                  <div className="card-item-body">{item.prefix}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('firmware_context.latest_version')}</div>
                  <div className="card-item-body">{item.latestVersion}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('reports.total_devices')}</div>
                  <div className="card-item-body">{item.totalDevices}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('reports.updated_devices')}</div>
                  <div className="card-item-body">{item.updatedDevices}</div>
                </div>
                <div className="card-item">
                  <div className="card-item-title">{t('reports.update_percentage')}</div>
                  <div className="card-item-body">{item.updatePercentage}</div>
                </div>
              </>
            )}
          </div>
        ))}
      </>
    )
  }

  const renderTableSection = () => (
    <div className="table-section">
      <div className="actions-wrapper">
        <SearchWithFilter onSearch={handleSearch} />
        <div className="table-actions-row">
          <SimpleButton className="action-button" onClick={() => handleDownload()} disabled={props.isLoading || !hasTable}>
            <DownloadIcon />
            {t('download')}
          </SimpleButton>
        </div>
      </div>
      {renderSoftwareVersionTable()}
      {statsVisibleCount < filteredTableData.length && (
        <div className="buttons-wrapper">
          <SimpleButton className="load-more-button" onClick={() => setStatsVisibleCount(statsVisibleCount + 10)}>
            {t('load_more')}
          </SimpleButton>
        </div>
      )}
    </div>
  )

  return (
    <div className="internal-reports-section software-version">
      <div className="actions-wrapper page-section">
        <SearchWithFilter
          showFiltersModal={() => setShowFiltersModal(true)}
          onResetFilters={() => handleResetFilters()}
          showCounterBadge={
            selectedFilterModel.length > 0 || selectedFilterRegion.length > 0 || selectedSoftwareType.length > 0 || selectedFilterPrefix.length > 0
          }
          counterBadge={selectedFilterModel.length + selectedFilterRegion.length + selectedSoftwareType.length + selectedFilterPrefix.length}
        />
      </div>
      <div className="disclaimer-box-text">
        <div className="icon-wrapper">
          <InformationIcon />
        </div>
        <div className="disclaimer-wrapper">
          <div className="title">{t('reports.date_disclaimer')}</div>
        </div>
      </div>
      {!hasChart && !hasTable && !isFirstLoad ? (
        <EmptyPageResults title={t('no_data_found')} subtitle={t('cannot_find_matching_search')} />
      ) : (
        <>
          {renderChartSection()}
          {renderTableSection()}
        </>
      )}
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('model'),
              type: FilterType.multiSelect,
              dataset: props.g4ModelNames,
              input: selectedFilterModel,
              output: (model) => setSelectedFilterModel(model),
            },
            {
              name: t('reports.software_type'),
              type: FilterType.multiSelect,
              dataset: typeLabels,
              input: selectedSoftwareType,
              output: (type) => {
                setSelectedSoftwareType(type)
              },
            },
            {
              name: t('region'),
              type: FilterType.multiSelect,
              dataset: getUniqueRegions(props.reportsRegions),
              input: selectedFilterRegion,
              output: (region) => setSelectedFilterRegion(region),
            },
            {
              name: t('prefix'),
              type: FilterType.multiSelect,
              dataset: props.filters.prefix || [],
              input: selectedFilterPrefix,
              output: (prefix) => setSelectedFilterPrefix(prefix),
            },
          ]}
          shouldFilter={(shouldApply) => {
            setShouldApplyFilters(shouldApply)
          }}
          resetFilters={handleResetFilters}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {props.isLoading && (
        <div className="spinner-wrapper">
          <LoadingSpinner />
        </div>
      )}
    </div>
  )
}

function stateToProps({ reports, shared, firmware }) {
  const modelData = firmware?.firmwareModels || []
  const firmwareModels = [...new Set(modelData.map((m) => m.model || '').filter(Boolean))]
  const g4ModelNames = [
    ...new Set(
      modelData
        .filter((m) => m.is_g4 || m.is_g4_plus)
        .map((m) => m.model || '')
        .filter(Boolean),
    ),
  ]

  return {
    softwareVersionStatistics: reports?.softwareVersionStatisticsData || [],
    softwareVersionRaw: reports?.softwareVersionRawData || [],
    reportsRegions: shared?.regions || [],
    firmwareModels: sortModelsAlphabetically(firmwareModels),
    filters: reports?.softwareVersionFilterOptions || {},
    types: firmware?.types || [],
    g4ModelNames: sortModelsAlphabetically(g4ModelNames),
    isLoading: reports?.isLoading,
    canLoadMore: reports?.canLoadMoreTableData,
    errorMessage: reports?.errorMessage,
    successMessage: reports?.successMessage,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadSoftwareVersionRawData,
        loadSoftwareVersionStatisticsData,
        downloadSoftwareVersionReportsCSV,
        loadSoftwareVersionReportsFilterOptions,
        clearReportsMessages,
        loadFirmwareModels,
        loadFirmwareTypes,
        loadRegions,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(DeviceSoftwareVersionReports)

